import { useMemo, useState } from 'preact/hooks';
import { IconWrap, type IconName } from '../components/Icon.tsx';
import { asLevel, type Level } from '../lib/levels.ts';
import { progressionForLevel, type SubjectProgress } from '../lib/progression.ts';
import { hasReferentiel } from '../lib/referentiel.ts';
import { studentDisplayName, studentNumbers } from '../lib/student-roster.ts';
import { SUBJECTS } from '../lib/subjects.ts';
import { live, useStore } from '../store/store.ts';
import { openModal } from '../ui/ui-store.ts';
import { SkillModal } from '../features/misc/SimpleModals.tsx';
import { ReferentielProgressModal } from '../features/misc/ReferentielProgressModal.tsx';

/**
 * Suivi: how much of the year's référentiel the lessons have actually covered.
 *
 * The bars used to count hand-encoded skills, which meant an empty 0/0 on a
 * page whose whole point is knowing what is left to teach. They now count the
 * attendus tagged on lessons against the tronc commun itself, so 100 % means
 * every contenu d'apprentissage of the year has been worked on at least once.
 * The hand-encoded skills stay as they were — they track individual pupils,
 * which is a different question.
 */
export function FollowPage() {
  const level = asLevel(useStore((s) => s.settings.className));
  const lessons = live(useStore((s) => s.lessons));
  const skills = live(useStore((s) => s.skills));
  const students = live(useStore((s) => s.students));
  const [view, setView] = useState<'skills' | 'students'>('skills');

  const progression = useMemo(() => progressionForLevel(level, lessons), [level, lessons]);
  const bySubject = useMemo(
    () => new Map(progression.subjects.map((s) => [s.subject, s])),
    [progression],
  );

  const acquired = skills.filter((k) => k.status === 'Acquis').length;

  return (
    <section class={`page ${view === 'students' ? 'printStudentSheet' : ''}`}>
      <div class="classTabs followTabs" role="tablist" aria-label="Vue du suivi">
        <button
          class={view === 'skills' ? 'active' : undefined}
          role="tab"
          aria-selected={view === 'skills'}
          onClick={() => setView('skills')}
        >
          Compétences
        </button>
        <button
          class={view === 'students' ? 'active' : undefined}
          role="tab"
          aria-selected={view === 'students'}
          onClick={() => setView('students')}
        >
          Liste des élèves <span>{students.length}</span>
        </button>
      </div>

      {view === 'students' ? (
        <StudentSheet students={students} />
      ) : (
        <>
          <div class="sectionTitle">
            <div>
              <h1>Suivi des compétences</h1>
              <p class="muted">
                {hasReferentiel(level)
                  ? `Couverture du référentiel ${level}, d’après les compétences choisies dans les leçons.`
                  : `Le référentiel n’est pas encore disponible pour la ${level}.`}
              </p>
            </div>
            <button
              class="btn primary"
              onClick={() => openModal('Ajouter une compétence', <SkillModal />)}
            >
              + Compétence
            </button>
          </div>

      <div class="stats" style={{ marginBottom: '16px' }}>
        <div class="stat">
          <strong>{progression.percent}%</strong>
          <span class="muted small">Référentiel couvert</span>
        </div>
        <div class="stat">
          <strong>
            {progression.donePoints}
            <span class="muted" style={{ fontSize: '0.7em' }}> / {progression.totalPoints}</span>
          </strong>
          <span class="muted small">Contenus d’apprentissage</span>
        </div>
        <div class="stat">
          <strong>
            {progression.coveredAttendus}
            <span class="muted" style={{ fontSize: '0.7em' }}> / {progression.totalAttendus}</span>
          </strong>
          <span class="muted small">Attendus travaillés</span>
        </div>
        <div class="stat">
          <strong>{acquired}</strong>
          <span class="muted small">Compétences acquises</span>
        </div>
      </div>

      {!hasReferentiel(level) && (
        <p class="empty" style={{ marginBottom: '16px' }}>
          Seule la P3 dispose du référentiel du tronc commun pour l’instant. Change la classe dans
          « Compte » pour suivre la progression.
        </p>
      )}

          <div class="subjectGrid">
            {SUBJECTS.map(([name, bg], i) => (
              <SubjectCard
                key={name}
                background={bg}
                icon={i % 2 ? 'target' : 'book'}
                progress={bySubject.get(name)}
                skillCount={skills.filter((k) => k.subject === name).length}
                level={level}
                name={name}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

const SHEET_COLUMNS = ['Note 1', 'Note 2', 'Note 3', 'Note 4', 'Remarques'] as const;

function StudentSheet({ students }: { students: readonly { id: string; name: string }[] }) {
  // The printed sheet is a class list, so it runs in the numbering order and
  // shows the numbers the rest of the app shows.
  const numbers = useMemo(() => studentNumbers(students), [students]);
  const roster = useMemo(
    () => [...students].sort((a, b) => (numbers.get(a.id) ?? 0) - (numbers.get(b.id) ?? 0)),
    [students, numbers],
  );

  return (
    <>
      <div class="sectionTitle studentSheetTitle">
        <div>
          <h1>Liste des élèves</h1>
          <p class="muted">
            Complète les cases à l’écran ou imprime ce tableau pour écrire à la main.
          </p>
        </div>
        <button class="btn primary" onClick={() => window.print()}>
          Imprimer le tableau
        </button>
      </div>

      {students.length ? (
        <div class="studentSheetCard">
          <table class="studentSheetTable">
            <thead>
              <tr>
                <th scope="col" class="studentSheetNumber">N°</th>
                <th scope="col">Élève</th>
                {SHEET_COLUMNS.map((column) => (
                  <th scope="col" key={column}>{column}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {roster.map((student) => (
                <tr key={student.id}>
                  <td class="studentSheetNumber">{numbers.get(student.id)}</td>
                  <th scope="row">{studentDisplayName(student.name)}</th>
                  {SHEET_COLUMNS.map((column) => (
                    <td key={column}>
                      <input aria-label={`${column} — ${studentDisplayName(student.name)}`} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div class="empty">Aucun élève dans la classe. Ajoute-les depuis l’onglet « Classe ».</div>
      )}
    </>
  );
}

interface CardProps {
  name: string;
  background: string;
  icon: IconName;
  progress: SubjectProgress | undefined;
  skillCount: number;
  level: Level;
}

function SubjectCard({ name, background, icon, progress, skillCount, level }: CardProps) {
  const total = progress?.totalPoints ?? 0;
  const done = progress?.donePoints ?? 0;
  const percent = progress?.percent ?? 0;
  const attendus = progress?.totalAttendus ?? 0;
  const coveredAttendus = progress?.coveredAttendus ?? 0;

  return (
    <div class="subject">
      <div class="subjectTitle">
        <IconWrap name={icon} background={background} />
        <span>{name}</span>
      </div>

      {total === 0 ? (
        <>
          <div class="subjectCount">
            <span class="muted">—</span>
          </div>
          <div class="progress">
            <span style={{ width: '0%' }} />
          </div>
          <div class="small muted">
            {hasReferentiel(level)
              ? 'Hors tronc commun'
              : 'Référentiel indisponible pour cette classe'}
          </div>
        </>
      ) : (
        <>
          <div class="subjectCount">
            <span>
              {done} <span class="muted subjectCountTotal">/ {total}</span>
            </span>
            <span class="muted subjectCountTotal">{percent}%</span>
          </div>

          <div class="progress">
            <span style={{ width: `${percent}%` }} />
          </div>

          <div class="small muted">
            {total - done} contenu{total - done > 1 ? 's' : ''} à couvrir ·{' '}
            {coveredAttendus}/{attendus} attendus
          </div>
        </>
      )}

      <div class="subjectActions">
        {total > 0 && (
          <button
            class="btn soft"
            onClick={() =>
              openModal(
                `Référentiel ${level} — ${name}`,
                <ReferentielProgressModal subject={name} level={level} />,
              )
            }
          >
            Détail
          </button>
        )}
        <button
          class="btn soft"
          onClick={() => openModal('Ajouter une compétence', <SkillModal subject={name} />)}
        >
          Compétence{skillCount > 0 ? ` (${skillCount})` : ''}
        </button>
      </div>
    </div>
  );
}
